import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useMotionEnabled } from '../lib/motion';

/**
 * Scoreboard countdown (DD HH MM SS with white-bar separators). Ticks
 * against a drift-corrected clock: `serverOffsetMs` (server minus local at
 * fetch) keeps the display honest even when the visitor's clock is wrong.
 *
 * Each digit animates on change — roll up with a brief neon flicker —
 * via keyed AnimatePresence spans. Reduced motion swaps digits in place.
 */
type CountdownTimerProps = {
  showStartAt: string;
  serverOffsetMs: number;
  /** Font-size class for the scoreboard row (default = hero scale). */
  sizeClass?: string;
  /** Glow class for the row (default = the hero's .glow-timer). */
  glowClass?: string;
  /** Boot the scoreboard: hold 00s for a beat on first mount, then roll
   *  every digit to the real time at once — the board powering on. Used by
   *  the hero's arrival choreography; ignored under reduced motion. */
  boot?: boolean;
};

const ZERO_PARTS: Parts = { days: '00', hours: '00', minutes: '00', seconds: '00' };

type Parts = { days: string; hours: string; minutes: string; seconds: string };

function remainingParts(showStartAt: string, serverOffsetMs: number): Parts {
  const target = new Date(showStartAt).getTime();
  const now = Date.now() + serverOffsetMs;
  const total = Math.max(0, Math.floor((target - now) / 1000));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(Math.min(n, 99)).padStart(2, '0');
  return { days: pad(days), hours: pad(hours), minutes: pad(minutes), seconds: pad(seconds) };
}

function Digit({ value, animate }: { value: string; animate: boolean }) {
  if (!animate) {
    return <span className="inline-block w-[0.62em] text-center">{value}</span>;
  }
  return (
    <span className="relative inline-block h-[1.05em] w-[0.62em] overflow-hidden align-baseline">
      <AnimatePresence initial={false} mode="popLayout">
        <motion.span
          key={value}
          initial={{ y: '-55%', opacity: 0 }}
          animate={{ y: '0%', opacity: 1 }}
          exit={{ y: '55%', opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="digit-flicker absolute inset-0 text-center"
        >
          {value}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

function DigitGroup({ value, animate }: { value: string; animate: boolean }) {
  return (
    <span className="whitespace-nowrap">
      <Digit value={value[0]!} animate={animate} />
      <Digit value={value[1]!} animate={animate} />
    </span>
  );
}

function Separator() {
  return (
    <img
      src="/assets/images/ornaments/white-bar.svg"
      alt=""
      className="h-[0.82em] w-auto shrink-0"
    />
  );
}

export function CountdownTimer({
  showStartAt,
  serverOffsetMs,
  sizeClass = '[font-size:clamp(3.1rem,11vw,10.625rem)]',
  glowClass = 'glow-timer',
  boot = false,
}: CountdownTimerProps) {
  const motionOn = useMotionEnabled();
  const bootRoll = boot && motionOn;
  const booted = useRef(false);
  const [parts, setParts] = useState<Parts>(() =>
    bootRoll ? ZERO_PARTS : remainingParts(showStartAt, serverOffsetMs),
  );

  useEffect(() => {
    const tick = () => setParts(remainingParts(showStartAt, serverOffsetMs));
    let intervalId: number | undefined;
    let bootId: number | undefined;
    const start = () => {
      tick();
      intervalId = window.setInterval(tick, 1000);
    };
    if (bootRoll && !booted.current) {
      // Hold the 00s for a beat so the first tick reads as the scoreboard
      // powering on (every digit rolls at once). First mount only — poll
      // refreshes must not re-boot a running board.
      booted.current = true;
      bootId = window.setTimeout(start, 350);
    } else {
      start();
    }
    return () => {
      if (bootId !== undefined) window.clearTimeout(bootId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [showStartAt, serverOffsetMs, bootRoll]);

  const label = useMemo(
    () =>
      `Show starts in ${Number(parts.days)} days, ${Number(parts.hours)} hours, ` +
      `${Number(parts.minutes)} minutes`,
    [parts.days, parts.hours, parts.minutes],
  );

  return (
    <div role="timer" aria-label={label} className="w-full">
      {/* Row width ≈ 6.6em (8 digits × 0.62em + separators + em gaps), so the
          font cap keeps the full row inside the 1200px content column. */}
      <div
        aria-hidden="true"
        className={`${glowClass} flex items-center justify-center gap-[0.22em] font-display leading-none text-brand-white ${sizeClass}`}
      >
        <DigitGroup value={parts.days} animate={motionOn} />
        <Separator />
        <DigitGroup value={parts.hours} animate={motionOn} />
        <Separator />
        <DigitGroup value={parts.minutes} animate={motionOn} />
        <Separator />
        <DigitGroup value={parts.seconds} animate={motionOn} />
      </div>
    </div>
  );
}
